









































import { Component, Vue, Prop } from "vue-property-decorator";

@Component
export default class VideoPlayer extends Vue {
  @Prop({ required: false, default: null })
  src: string | null;

  @Prop({ required: false })
  audioOutput: AudioNode | null;

  @Prop({ required: false })
  audioCtx: AudioContext | null;

  @Prop({ required: true })
  enableControls!: boolean;

  // stores the duration of the video once we've loaded it
  duration = 0;

  // stores the current video time as updated by the `ontimeupdate` event to
  // get reactivity. Do not write to this field elsewhere.
  currentTime = 0;

  // we start in the playing state, though we'll wait for the
  // `oncanplaythrough` event to actually play the video
  playing = true;

  scrubbing = false;

  mounted() {
    const video = this.$refs.video as HTMLMediaElement;
    if (this.audioCtx || this.audioOutput) {
      const videoSource = this.audioCtx.createMediaElementSource(video);
      if (this.audioCtx) {
        videoSource.connect(this.audioCtx.destination);
      }
      if (this.audioOutput) {
        videoSource.connect(this.audioOutput);
      }
    } else {
      video.muted = true;
    }

    // Wait until the video has loaded sufficiently to actually start playing
    video.oncanplaythrough = () => {
      // We never want to play automatically if we're in the paused state
      // We also don't want to play if we're in the middle of scrubbing
      if (!this.playing || this.scrubbing) return;
      this.play();
    };

    video.onloadedmetadata = () => {
      this.duration = video.duration;
      this.$emit("loadedmetadata", video.duration);
    };

    video.onplay = () => {
      this.$emit("play");
    };

    video.onpause = () => {
      this.$emit("pause");
    };

    video.onended = () => {
      this.$emit("ended");
      this.playing = false;
    };

    video.ontimeupdate = () => {
      this.currentTime = video.currentTime;
      // Avoid excessive emits when scrubbing
      if (!this.scrubbing) {
        this.$emit("timeupdate", video.currentTime);
      }
    };
  }

  play() {
    return (this.$refs.video as HTMLMediaElement).play();
  }

  pause() {
    return (this.$refs.video as HTMLMediaElement).pause();
  }

  forcePause() {
    this.playing = false;
    this.pause();
  }

  forcePlay() {
    this.playing = true;
    this.play();
  }

  private toggle() {
    if (!this.enableControls) return;
    if (this.playing) {
      this.forcePause();
    } else {
      this.forcePlay();
    }
  }

  private startScrub() {
    if (!this.enableControls) return;
    // Note that we're currently scrubbing to avoid excessive
    // UI churn during video updates
    this.scrubbing = true;
    // We want to pause the video during scrubbing to avoid competeing
    // with `ontimeupdate`.
    this.pause();
  }

  private stopScrub() {
    if (!this.enableControls) return;
    this.scrubbing = false;
    // Resume playing if we're meant to be in the playing state
    if (this.playing) {
      this.play();
    }
  }

  private changeScrubPosition(event: Event) {
    if (!this.enableControls) return;
    const video = this.$refs.video as HTMLMediaElement;
    video.currentTime = (event.target as HTMLInputElement).valueAsNumber;
  }

  private formatSeconds(s: number) {
    function addLeadingZero(n: number) {
      return n < 10 ? `0${n}` : `${n}`;
    }
    if (!s) "00:00";
    const MM = addLeadingZero(Math.floor(s / 60));
    const SS = addLeadingZero(Math.floor(s % 60));
    return `${MM}:${SS}`;
  }

  setCurrentTime(t: number) {
    (this.$refs.video as HTMLMediaElement).currentTime = t;
  }

  // We gotta clean up after ourselves
  beforeDestroy() {
    const video = this.$refs.video as HTMLMediaElement;
    // this magic incantation seems to actually make sure the video element is
    // removed and garbage collected, which is important since it can occupy a
    // fair amount of memory.
    video.pause();
    video.removeAttribute("src");
    video.load();
  }
}
